// Salesforce id helpers shared by the server (BFF proxy/guard) and the UI. Lives in the cross-env
// `src/modules/utilities` so both packages can import it via `~/` (the UI-package copy isn't on the
// server's path).

const FIFTEEN_CHAR_ID_LENGTH = 15;
const CASE_TABLE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ012345';

// A Salesforce Core org id: the `00D` prefix plus 12 more chars (15-char form), optionally followed
// by the 3-char case-safe checksum suffix (18-char form). Anchored so it rejects anything that isn't
// a whole, well-formed org id — including tenants from non-Core products (Slack, Tableau, etc.) whose
// ids don't take the `00D` key-prefix shape.
const ORG_ID_PATTERN = /^00D[A-Za-z0-9]{12}([A-Za-z0-9]{3})?$/;

/**
 * True when `id` is a syntactically valid 15- or 18-char Salesforce Core org id (`00D` key prefix).
 * Used to keep only Salesforce Core orgs out of a mixed multi-product tenant list.
 */
export function isValidSalesforceOrgId(id: unknown): id is string {
    return typeof id === 'string' && ORG_ID_PATTERN.test(id);
}

/**
 * Canonicalizes a 15-char Salesforce id to its 18-char case-insensitive form (appends the checksum
 * suffix). An id already 18 chars (or any other length) is returned unchanged, so callers can
 * canonicalize once and compare without caring which form they were given.
 */
export function toEighteenCharId(id: string): string {
    if (id?.length !== FIFTEEN_CHAR_ID_LENGTH) {
        return id;
    }
    let suffix = '';
    for (let chunk = 0; chunk < FIFTEEN_CHAR_ID_LENGTH; chunk += 5) {
        let bits = '';
        for (let offset = 4; offset >= 0; offset--) {
            const char = id.charAt(chunk + offset);
            bits += char >= 'A' && char <= 'Z' ? '1' : '0';
        }
        suffix += CASE_TABLE.charAt(parseInt(bits, 2));
    }
    return id + suffix;
}
